import { safeGetLocalStorage, safeSetLocalStorage } from '@/lib/utils/storageUtils';

export type CanonicalTheme =
  'device' | 'light' | 'dark' | 'history' | 'buzzfeed' | 'indyweek' | 'assembly';

const CANONICAL_THEMES: readonly CanonicalTheme[] = [
  'device',
  'light',
  'dark',
  'history',
  'buzzfeed',
  'indyweek',
  'assembly',
];

export const parseThemeFromString = (value: string | null | undefined): CanonicalTheme | null => {
  if (!value) return null;
  const normalized = String(value).trim().toLowerCase();
  return (CANONICAL_THEMES as readonly string[]).includes(normalized)
    ? (normalized as CanonicalTheme)
    : null;
};

export const getSystemTheme = (): CanonicalTheme => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

const isIframeView = (): boolean =>
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('view') === 'iframe';

export const persistThemePreference = (theme: CanonicalTheme): void => {
  // Same-origin previews share storage with the Portal, but own only their displayed theme.
  if (!isIframeView()) safeSetLocalStorage('theme', theme);
};

export const getInitialTheme = (): CanonicalTheme => {
  // Guard against SSR - return 'device' to match client default and avoid hydration mismatch
  if (typeof window === 'undefined') {
    return 'device';
  }

  // If we are rendering as an embed/iframe, we MUST ignore session/local storage
  // and start from a neutral light foundation until the runtime preset resolves.
  if (isIframeView()) {
    return 'light';
  }

  const saved = parseThemeFromString(safeGetLocalStorage('theme'));
  if (saved) return saved;

  // Default to respecting device preference on first load
  return 'device';
};
